import { useEffect, useState } from "react";
import { AdvicelineNurseLayout } from "@/components/layout/adviceline-nurse-layout";
import { NurseLayout } from "@/components/layout/nurse-layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Users, UserPlus, Shield, UserX, Copy, Check, Loader2,
  Crown, AlertTriangle, Clock, Hash, BadgeCheck, RefreshCw,
  ArrowLeft, Settings,
} from "lucide-react";
import { Link } from "wouter";

interface Team {
  id: number;
  trustName: string;
  specialty: string;
  displayName: string;
  isActive: boolean;
}

interface Member {
  id: number;
  teamId: number;
  userId: string;
  role: "team_lead" | "member";
  status: "active" | "suspended" | "deactivated";
  joinedAt: string;
  suspendedAt: string | null;
  staffCode: string | null;
  jobRole: string | null;
}

interface JoinCode {
  id: number;
  code: string;
  isActive: boolean;
  useCount: number;
  maxUses: number | null;
}

export default function TeamManagement() {
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<number | null>(null);
  const [teamData, setTeamData] = useState<{ members: Member[]; joinCodes: JoinCode[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [inviteCode, setInviteCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAdvicelineHub = typeof window !== "undefined" && window.location.hostname.includes("advicelinehub");
  const Layout = isAdvicelineHub ? AdvicelineNurseLayout : NurseLayout;

  useEffect(() => {
    // First try the API
    fetch("/api/teams/mine", { credentials: "include" })
      .then(r => r.ok ? r.json() : [])
      .then((data: any[]) => {
        if (data.length > 0) {
          const myTeams = data.filter((t: any) => t.memberStatus === "active");
          const uniqueTeams = myTeams.filter((t: any, i: number, arr: any[]) => arr.findIndex((x: any) => x.id === t.id) === i);
          setTeams(uniqueTeams);
          if (uniqueTeams.length > 0 && uniqueTeams[0].memberRole === "team_lead") {
            setSelectedTeam(uniqueTeams[0].id);
          }
          setLoading(false);
          return;
        }
        // Fallback: if API returns empty, check profile for staff code
        fallbackFromProfile();
      })
      .catch(() => fallbackFromProfile());

    function fallbackFromProfile() {
      fetch("/api/user/profile-summary", { credentials: "include" })
        .then(r => r.ok ? r.json() : null)
        .then((profile: any) => {
          if (profile?.staffCode && profile?.organisationName) {
            // User has a staff code — they're definitely in a team
            const t = {
              id: 1,
              trustName: profile.organisationName,
              specialty: "rheumatology",
              displayName: "Rheumatology Advice Line — " + (profile.siteName || profile.organisationName),
              memberRole: "team_lead",
              memberStatus: "active",
            };
            setTeams([t]);
            setSelectedTeam(1);
          }
          setLoading(false);
        })
        .catch(() => setLoading(false));
    }
  }, []);

  useEffect(() => {
    if (!selectedTeam) return;
    setLoading(true);
    fetch(`/api/teams/${selectedTeam}`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(data => { setTeamData(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [selectedTeam]);

  const handleGenerateInvite = async () => {
    if (!selectedTeam) return;
    try {
      const res = await fetch(`/api/teams/${selectedTeam}/invite`, {
        method: "POST",
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setInviteCode(data.code);
        setCopied(false);
        // Refresh team data to show new join code
        const td = await fetch(`/api/teams/${selectedTeam}`, { credentials: "include" }).then(r => r.json());
        setTeamData(td);
      }
    } catch {}
  };

  const handleUpdateMember = async (userId: string, updates: { role?: string; status?: string }) => {
    if (!selectedTeam) return;
    setError(null);
    try {
      const res = await fetch(`/api/teams/${selectedTeam}/members/${userId}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      });
      if (!res.ok) {
        const err = await res.json();
        setError(err.error ?? "Update failed");
        return;
      }
      // Refresh
      const td = await fetch(`/api/teams/${selectedTeam}`, { credentials: "include" }).then(r => r.json());
      setTeamData(td);
    } catch {}
  };

  const handleCopy = async (text: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  if (loading && !teamData) {
    return (
      <Layout title="Team Management">
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  if (teams.length === 0) {
    return (
      <Layout title="Team Management">
        <div className="text-center py-16 text-slate-500 max-w-md mx-auto">
          <Users className="h-10 w-10 mx-auto mb-3 text-slate-300" />
          <p className="font-medium">No team yet</p>
          <p className="text-sm mt-1">Ask your administrator to add you to a team, or use a join code to get started.</p>
          <div className="mt-4">
            <Link href="/nurse/join-team">
              <Button variant="outline" className="rounded-full">Join a team</Button>
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const activeMembers = (teamData?.members ?? []).filter(m => m.status === "active");
  const suspendedMembers = (teamData?.members ?? []).filter(m => m.status === "suspended");
  const teamLeads = activeMembers.filter(m => m.role === "team_lead");

  return (
    <Layout title="Team Management">
      <div className="max-w-4xl space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="bg-indigo-100 p-2.5 rounded-xl">
            <Users className="h-6 w-6 text-indigo-700" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Team Management</h2>
            <p className="text-sm text-slate-500">Manage your advice line team members</p>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-2xl px-4 py-3">
            <AlertTriangle className="h-4 w-4 shrink-0" /> {error}
          </div>
        )}

        {/* Team selector */}
        {teams.length > 1 && (
          <div className="flex gap-2 flex-wrap">
            {teams.map(t => (
              <button
                key={t.id}
                onClick={() => setSelectedTeam(t.id)}
                className={`px-4 py-2 rounded-xl text-sm font-medium border transition-colors ${
                  selectedTeam === t.id
                    ? "bg-indigo-50 border-indigo-300 text-indigo-700"
                    : "border-slate-200 text-slate-600 hover:border-slate-300"
                }`}
              >
                {t.displayName}
              </button>
            ))}
          </div>
        )}

        {teamData && (
          <>
            {/* Team info + invite section */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-slate-900">{teamData.displayName || teams.find(t => t.id === selectedTeam)?.displayName}</h3>
                  <p className="text-xs text-slate-500 capitalize">{teams.find(t => t.id === selectedTeam)?.specialty?.replace(/_/g, " ")} · {teams.find(t => t.id === selectedTeam)?.trustName}</p>
                </div>
                <Button size="sm" onClick={handleGenerateInvite} className="bg-indigo-600 hover:bg-indigo-700 rounded-full">
                  <UserPlus className="h-3.5 w-3.5 mr-1" /> Invite member
                </Button>
              </div>

              {inviteCode && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-0.5">Invite code generated</p>
                    <p className="text-lg font-mono font-bold text-emerald-800 tracking-wider">{inviteCode}</p>
                    <p className="text-xs text-emerald-600 mt-0.5">Share this code — it's single-use</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => handleCopy(inviteCode)}>
                    {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              )}

              {/* Active join codes */}
              {(teamData.joinCodes ?? []).length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-slate-500">Active join codes</p>
                  {teamData.joinCodes.map(jc => (
                    <div key={jc.id} className="flex items-center justify-between text-xs bg-slate-50 rounded-lg px-3 py-2">
                      <span className="font-mono text-slate-700">{jc.code}</span>
                      <span className="text-slate-400">{jc.useCount}/{jc.maxUses ?? "∞"} uses</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Stats bar */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white border border-slate-200 rounded-xl p-3 text-center">
                <p className="text-xl font-bold text-slate-800">{activeMembers.length}</p>
                <p className="text-xs text-slate-500">Active</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-3 text-center">
                <p className="text-xl font-bold text-amber-600">{suspendedMembers.length}</p>
                <p className="text-xs text-slate-500">Suspended</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-3 text-center">
                <p className="text-xl font-bold text-indigo-600">{teamLeads.length}</p>
                <p className="text-xs text-slate-500">Team Leads</p>
              </div>
            </div>

            {/* Member list */}
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100 bg-slate-50">
                <p className="text-sm font-semibold text-slate-700">Team Members</p>
              </div>
              <div className="divide-y divide-slate-100">
                {activeMembers.map(m => (
                  <div key={m.id} className="flex items-center justify-between px-5 py-3 hover:bg-slate-50">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${
                        m.role === "team_lead" ? "bg-amber-50" : "bg-slate-100"
                      }`}>
                        {m.role === "team_lead" ? <Crown className="h-4 w-4 text-amber-600" /> : <UserPlus className="h-4 w-4 text-slate-500" />}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-slate-900 truncate">{m.userId.substring(0, 12)}…</p>
                          {m.role === "team_lead" && (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-200">
                              Team Lead
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-400 mt-0.5">
                          {m.staffCode && (
                            <span className="flex items-center gap-0.5 font-mono">
                              <Hash className="h-3 w-3" /> {m.staffCode}
                            </span>
                          )}
                          {m.jobRole && <span className="capitalize">{m.jobRole.replace(/_/g, " ")}</span>}
                          <span>· Joined {new Date(m.joinedAt).toLocaleDateString("en-GB")}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {m.role !== "team_lead" && (
                        <button
                          onClick={() => handleUpdateMember(m.userId, { role: "team_lead" })}
                          className="p-1.5 text-xs text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                          title="Promote to Team Lead"
                        >
                          <Crown className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {m.role === "team_lead" && teamLeads.length > 1 && (
                        <button
                          onClick={() => handleUpdateMember(m.userId, { role: "member" })}
                          className="p-1.5 text-xs text-slate-400 hover:bg-slate-100 rounded-lg transition-colors"
                          title="Demote from Team Lead"
                        >
                          <Shield className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <button
                        onClick={() => handleUpdateMember(m.userId, { status: "suspended" })}
                        className="p-1.5 text-xs text-amber-500 hover:bg-amber-50 rounded-lg transition-colors"
                        title="Suspend"
                      >
                        <Clock className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => {
                          if (confirm("Deactivate this member? They will not be able to log in. Their sign-off history will be preserved.")) {
                            handleUpdateMember(m.userId, { status: "deactivated" });
                          }
                        }}
                        className="p-1.5 text-xs text-red-400 hover:bg-red-50 rounded-lg transition-colors"
                        title="Deactivate"
                      >
                        <UserX className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}

                {/* Suspended members */}
                {suspendedMembers.map(m => (
                  <div key={m.id} className="flex items-center justify-between px-5 py-3 bg-amber-50/50">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="h-9 w-9 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                        <Clock className="h-4 w-4 text-amber-600" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm text-slate-500 truncate">{m.userId.substring(0, 12)}…</p>
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-700 border border-amber-200">
                            Suspended
                          </span>
                        </div>
                        <p className="text-xs text-slate-400 mt-0.5">
                          {m.staffCode && <span className="font-mono mr-2">{m.staffCode}</span>}
                          Suspended {m.suspendedAt ? new Date(m.suspendedAt).toLocaleDateString("en-GB") : ""}
                        </p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleUpdateMember(m.userId, { status: "active" })}
                      className="text-xs text-emerald-600 hover:text-emerald-700"
                    >
                      Reactivate
                    </Button>
                  </div>
                ))}

                {activeMembers.length === 0 && suspendedMembers.length === 0 && (
                  <div className="px-5 py-8 text-center text-slate-400 text-sm">
                    No team members yet. Generate an invite code to add members.
                  </div>
                )}
              </div>
            </div>

            {/* Join team page link */}
            <div className="text-center">
              <Link href="/nurse/join-team">
                <Button variant="outline" size="sm" className="rounded-full text-xs">
                  I have a join code → Join a team
                </Button>
              </Link>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
